import styled, { css } from 'styled-components';
import { backgroundGradient } from '../../../commonStyles/backgroundGradient';
import { twoLayeredBoxShadow } from '../../../commonStyles/styling';
import colors from '../../../helpers/colors';
import fonts from '../../../helpers/fonts';
import mediaQuery from '../../../helpers/mediaQuery';
import spacing from '../../../helpers/spacing';
import { PrimaryCta, SecondaryCta } from '../../Cta';
import { Grid, GridItem } from '../../Grid';
import Icon from '../../Icon';
import Image from '../../Image';
import Link from '../../Link';
import Lottie from '../../Lottie';
import RichText from '../../RichText';

export type PageType = 'primary' | 'secondary';

type HeroStyleProps = {
  pageType?: PageType;
};

const ICON_SIZE = 1.25;

export const PageTitle = styled.div`
  ${fonts.headingXSmall}
  & {
    color: ${colors.neutral08};
  }
  margin: 0;
`;

export const Heading = styled.div`
  ${fonts.headingLarge}
  & {
    color: ${colors.neutral08};
  }

  margin: ${spacing(1)} 0 0;

  ${mediaQuery.tabletLandscape`
    margin-top: ${spacing(2)};
  `}
`;

export const RichTextSubhead = styled(RichText)`
  ${fonts.paragraphLarge}
  & {
    color: ${colors.neutral08};
  }

  margin: ${spacing(1)} 0 0;

  ${Icon} {
    width: ${spacing(ICON_SIZE)};
    height: ${spacing(ICON_SIZE)};
    margin-bottom: ${spacing(0.75)}; /* align icon to baseline */

    > * {
      position: relative;
    }

    svg {
      fill: ${colors.neutral08};
    }
  }

  > :first-child {
    margin-top: 0;
  }

  /* Override the custom bullet styles to allow for a special sized bullet */
  ul > li {
    margin: ${spacing(1)} 0;
    padding-left: ${spacing(ICON_SIZE + 1)};

    ${mediaQuery.tabletLandscape`
      margin: ${spacing(2)} 0;
      padding-left: ${spacing(ICON_SIZE - 1)};
    `}

    .bullet {
      vertical-align: baseline;
      left: ${spacing(-1)};
      margin-left: ${spacing(-ICON_SIZE)};
    }
  }

  && a {
    color: ${colors.neutral08};
    &:focus-visible,
    &:active {
      box-shadow: ${twoLayeredBoxShadow(colors.core01, colors.neutral08)};
    }
  }
`;

export const TrustSymbol = styled(Image)`
  margin: ${spacing(1)} 0 0;
  width: auto;
  height: ${spacing(6)};

  ${mediaQuery.tabletPortrait`
    height: ${spacing(8)};
  `}

  /* On desktop, if space allows the trust symbol should be placed off grid, 32px from the top and right.

    We approximate this by assuming that when the screen size >= 1440px, the trust symbol will fit off grid.
    Otherwise, it does not. This specific number is chosen because it works and is the same as the subtriangle width.
   */
  @media (min-width: 1440px) {
    position: absolute;
    top: ${spacing(4)};
    right: calc(50% - 720px + ${spacing(4)});
    margin: 0;
  }
`;

const mainImageStyling = css<HeroStyleProps>`
  margin: ${spacing(2)} 0 0;

  max-height: 200px;

  ${mediaQuery.tabletPortrait`
    margin: ${spacing(3)} 0 0;
    max-height: 354px;
  `}

  ${mediaQuery.tabletLandscape`
    margin: 0;
    max-height: ${({ pageType }) => (pageType === 'primary' ? '544px' : '352px')};
  `}
`;

export const MainImage = styled(Image)<HeroStyleProps>`
  ${mainImageStyling}
`;

export const MainAnimation = styled(Lottie)<HeroStyleProps>`
  ${mainImageStyling}
`;

export const ImageWrapper = styled.div`
  ${backgroundGradient};

  /* Increase the ImageWrapper box size so that the gradient covers a larger area and has the correct center

    Adding 33% (or 50%) width to the left causes the gradient center to be shifted left by 16.5% (or 25%) of the original width  
    Adding extra spacing to the top and bottom allows the gradient to extend slightly into the text, but not so much that
    the text fails accessibility tests. This extra top and bottom spacing is arbitrarily handpicked.
   */
  margin: ${spacing(-8)} 0 ${spacing(-8)} -33%;
  padding: ${spacing(8)} 0 ${spacing(8)} 33%;

  ${mediaQuery.tabletLandscape`
    /* There is no text above or below the image, so we can extend it further */
    margin: -50% 0 -50% -50%; 
    padding: 50% 0 50% 50%;
  `}
`;

export const CoBrandWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: ${spacing(2)} 0 0;
`;

export const CoBrand = styled.div`
  display: flex;
  padding: ${spacing(1)};

  background-color: ${colors.neutral08};
  border-radius: ${spacing(1)};
`;

export const CoBrandText = styled.span`
  display: inline-block;
  ${fonts.paragraphLarge}
  & {
    color: ${colors.neutral08};
  }

  white-space: nowrap;

  margin-right: ${spacing(2)};
`;

export const CoBrandImage = styled(Image)`
  display: inline-block;
  height: ${spacing(4)};
  width: auto;
  max-width: 194px;
`;

export const FinePrint = styled.p`
  ${fonts.paragraphSmall};
  & {
    color: ${colors.neutral08};
  }

  margin: ${spacing(1)} 0 0;

  ${mediaQuery.tabletLandscape`
    margin-top: ${spacing(2)};
  `}
`;

export const CtaWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: ${spacing(2)} 0 0 -${spacing(2)};

  > *:not(:only-child) {
    min-width: 0;
  }
`;

const styledCta = css`
  margin-left: ${spacing(2)};
  margin-top: ${spacing(2)};
`;

export const StyledSecondaryCta = styled(SecondaryCta)`
  ${styledCta};
`;

export const StyledPrimaryCta = styled(PrimaryCta)`
  ${styledCta};
`;

export const HeroGrid = styled(Grid)`
  padding: ${spacing(2)} 0 ${spacing(4)};

  ${mediaQuery.tabletPortrait`
    padding-top: ${spacing(3)}; 
  `}

  ${mediaQuery.tabletLandscape`
    padding-top: ${spacing(6)};
  `}
`;

/* The image is displayed on different places on desktop/tabletLandscape vs.
  mobile/tabletPortrait so we render both and use media queries to only show
  the applicable one. */
export const DesktopImageGridItem = styled(GridItem)`
  display: none;

  ${mediaQuery.tabletLandscape`
    display: block;
  `}
`;

export const ContentGridItem = styled(GridItem)`
  ${ImageWrapper} {
    ${mediaQuery.tabletLandscape`
      display: none;
    `}
  }
`;

export const TertiaryLink = styled(Link)`
  margin-top: ${spacing(4)};
  ${fonts.paragraph};
  text-decoration: underline;
`;
